package ai.jessie.web.projects

import ai.jessie.shared.projects.PositionInput
import ai.jessie.shared.projects.PositionResponse

internal val positionTypes = mapOf("FIXED_PRICE" to "Festpreis", "EFFORT" to "Aufwand")

internal fun activeLabel(active: Boolean) = if (active) "Aktiv" else "Inaktiv"

internal fun billableLabel(billable: Boolean) =
    if (billable) "Verrechenbar" else "Nicht verrechenbar"

// BR-052: the same value is a fixed amount or an hourly rate, depending on the position type.
internal fun priceLabel(type: String) =
    when (type) {
        "FIXED_PRICE" -> "Festpreis"
        "EFFORT" -> "Stundenansatz"
        else -> "Festpreis oder Stundenansatz"
    }

// Amounts and rates are shown exactly as the server or the member wrote them.
internal fun PositionInput.summary(vat: String?) =
    listOfNotNull(
            name,
            positionTypes[type] ?: "–",
            activeLabel(active),
            billableLabel(billable),
            unitPrice?.let { "${priceLabel(type)}: $it" },
            vat?.let { "MWST: $it" },
        )
        .joinToString(" · ")

internal fun PositionResponse.summary() = details.summary(vatName?.let { vatLabel(it, vatRate) })

internal fun vatLabel(name: String, rate: String?) = if (rate == null) name else "$name ($rate %)"

private val fieldLabels =
    mapOf(
        "name" to "Name",
        "description" to "Beschreibung",
        "customerId" to "Kunde",
        "positions" to "Positionen",
        "type" to "Art",
        "unitPrice" to "Festpreis oder Stundenansatz",
        "vatRateId" to "MWST-Satz",
    )

// Server field paths such as `positions[0].unitPrice` become «Position 1: Festpreis oder …».
internal fun projectFieldLabel(field: String): String {
    val leaf = fieldLabels[field.substringAfterLast('.')] ?: field
    val number = field.substringAfter('[', "").substringBefore(']').toIntOrNull()?.plus(1)
    return when {
        number != null -> "Position $number: $leaf"
        field.startsWith("positions.") -> "Position: $leaf"
        else -> leaf
    }
}

// A6, A7: the correction for each field and reason the server or the form identified.
internal fun projectCorrection(field: String, reason: String) =
    when (reason) {
        "REQUIRED" ->
            if (field == "positions") "Bitte mindestens eine Position erfassen."
            else if (field == "customerId") "Verrechenbare Projekte brauchen einen Kunden."
            else "Pflichtfeld."
        "TOO_LONG" ->
            if (field == "description") "Höchstens 500 Zeichen." else "Höchstens 200 Zeichen."
        "POSITIVE_DECIMAL_REQUIRED" ->
            "Bitte einen positiven Betrag mit höchstens acht Vorkomma- und zwei " +
                "Nachkommastellen eingeben, zum Beispiel 180.00."
        "MUST_BE_ABSENT" -> "Nicht verrechenbare Positionen haben weder Betrag noch MWST."
        "ACTIVE_REQUIRED" ->
            if (field == "customerId") "Bitte einen aktiven Kunden wählen."
            else "Bitte einen heute gültigen MWST-Satz wählen."
        "DUPLICATE" -> "Eine Position ist mehrfach enthalten. Bitte lade das Projekt neu."
        "EXPLICIT_REMOVAL_REQUIRED" ->
            "Bestehende Positionen müssen enthalten bleiben. Bitte lade das Projekt neu."
        else -> "Bitte prüfen."
    }

// B2: only ACTIVE customers are offered; an existing link, also to an archived customer, stays
// selectable so that an unrelated edit keeps it.
internal fun ProjectUiState.customerOptions(): Map<String, String> =
    listOfNotNull(current?.customerId?.let { it to (current.customerName ?: "–") }).toMap() +
        selections?.customers.orEmpty().associate { it.id to it.name }

// The rates valid on the organisation's current date, plus the rates the [stored] positions use.
internal fun ProjectUiState.vatOptions(stored: List<PositionResponse>): Map<String, String> =
    stored
        .mapNotNull { position ->
            val id = position.details.vatRateId
            val name = position.vatName
            if (id == null || name == null) null else id to vatLabel(name, position.vatRate)
        }
        .toMap() + selections?.vatRates.orEmpty().associate { it.id to vatLabel(it.name, it.rate) }

internal fun durationLabel(seconds: Long) =
    "${seconds / SECONDS_PER_HOUR} h ${seconds % SECONDS_PER_HOUR / SECONDS_PER_MINUTE} min"

private const val SECONDS_PER_HOUR = 3600
private const val SECONDS_PER_MINUTE = 60
